import { type FormEvent, useState } from 'react';
import type { LarguraMae, LarguraMaeNova, RetornoLinha } from '@pcp/contracts';
import { useGravarLote, useLarguraMae, useMaquinas, isOk } from '../../api/cadastros';
import { type Column, EditableTable } from '../../components/EditableTable';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { num } from './shared';

const c = t.cadastros.motherWidths;

const EMPTY: LarguraMaeNova = {
  maquina: '',
  'largura-mae': 1000,
  padrao: false,
  'refile-mm': 7.5,
  'max-pistas': 10,
  ativo: true,
};

export function LarguraMaeTab({ canEdit }: { canEdit: boolean }) {
  const query = useLarguraMae();
  const machines = useMaquinas();
  const lote = useGravarLote('largura-mae');
  const [draft, setDraft] = useState<LarguraMaeNova>(EMPTY);
  const [result, setResult] = useState<RetornoLinha | null>(null);
  const slitters = (machines.data ?? []).filter((m) => m.grupo === 'SLT');

  const columns: Column<LarguraMae>[] = [
    { key: 'maquina', header: c.machine, read: (r) => <strong>{r.maquina}</strong> },
    { key: 'largura-mae', header: c.width, numeric: true, read: (r) => num(r['largura-mae']) },
    {
      key: 'padrao',
      header: c.default,
      read: (r) => (r.padrao ? <span className="chip info">{t.cadastros.yes}</span> : t.cadastros.no),
      edit: { kind: 'check', field: 'padrao' },
    },
    {
      key: 'refile-mm',
      header: c.trim,
      numeric: true,
      read: (r) => num(r['refile-mm']),
      edit: { kind: 'number', field: 'refile-mm', step: 0.5, min: 0 },
    },
    {
      key: 'max-pistas',
      header: c.lanes,
      numeric: true,
      read: (r) => r['max-pistas'],
      edit: { kind: 'number', field: 'max-pistas', step: 1, min: 1 },
    },
    {
      key: 'ativo',
      header: c.active,
      read: (r) => (r.ativo ? t.cadastros.yes : t.cadastros.no),
      edit: { kind: 'check', field: 'ativo' },
    },
  ];

  const save = async (row: LarguraMae, changes: Partial<LarguraMae>) => {
    const [r] = await lote.mutateAsync({ metodo: 'PUT', linhas: [{ ...row, ...changes }] });
    return r!;
  };
  const remove = async (row: LarguraMae) => {
    const [r] = await lote.mutateAsync({ metodo: 'DELETE', linhas: [row] });
    return r!;
  };
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const [r] = await lote.mutateAsync({ metodo: 'POST', linhas: [draft] });
    setResult(r!);
    if (r && isOk(r)) setDraft(EMPTY);
  };

  return (
    <section className="panel" aria-labelledby="tab-mw">
      <h2 id="tab-mw">{t.cadastros.tabs.motherWidths}</h2>
      <p className="hint">{c.hint}</p>
      {query.isLoading ? <LoadingBlock /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data ? (
        <EditableTable
          rows={query.data}
          rowKey={(r) => `${r.maquina}|${r['largura-mae']}`}
          rowLabel={(r) => `${r.maquina} ${r['largura-mae']} mm`}
          columns={columns}
          canEdit={canEdit}
          onSave={save}
          onDelete={remove}
          onReload={() => void query.refetch()}
        />
      ) : null}

      {canEdit ? (
        <form onSubmit={(e) => void create(e)} className="new-row" aria-label={c.newTitle}>
          <strong>{c.newTitle}</strong>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="nw-maquina">{c.machine}</label>
              <select
                id="nw-maquina"
                className="select"
                value={draft.maquina}
                onChange={(e) => setDraft((d) => ({ ...d, maquina: e.target.value }))}
              >
                <option value="" />
                {slitters.map((m) => (
                  <option key={m.maquina} value={m.maquina}>
                    {m.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="nw-largura">{c.width}</label>
              <input
                id="nw-largura"
                type="number"
                value={draft['largura-mae']}
                onChange={(e) => setDraft((d) => ({ ...d, 'largura-mae': Number(e.target.value) }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nw-refile">{c.trim}</label>
              <input
                id="nw-refile"
                type="number"
                step="0.5"
                value={draft['refile-mm']}
                onChange={(e) => setDraft((d) => ({ ...d, 'refile-mm': Number(e.target.value) }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nw-pistas">{c.lanes}</label>
              <input
                id="nw-pistas"
                type="number"
                value={draft['max-pistas']}
                onChange={(e) => setDraft((d) => ({ ...d, 'max-pistas': Number(e.target.value) }))}
              />
            </div>
            <div className="field" style={{ alignSelf: 'end' }}>
              <button type="submit" className="btn" disabled={!draft.maquina || lote.isPending}>
                {t.cadastros.add}
              </button>
            </div>
          </div>
          {result && !isOk(result) ? (
            <div className="form-error" role="alert">
              {result.errordescription}
            </div>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
